<template>
  <controller>
    <page-header title="Radar de Matrículas" />

    <main class="tracking-matriculas">
      <div class="state error" v-if="error">
        <fa :icon="['far', 'triangle-exclamation']" />
        <span>{{ error }}</span>
      </div>

      <section class="global-filters">
        <div>
          <label class="date-range-field">
            Período
            <span class="date-range-control">
              <input
                type="date"
                v-model="filters.dataInicio"
                :min="minTrackingDate"
                :max="filters.dataFim || null"
                @change="normalizarPeriodo"
              />
              <i></i>
              <input
                type="date"
                v-model="filters.dataFim"
                :min="filters.dataInicio || minTrackingDate"
                @change="normalizarPeriodo"
              />
            </span>
          </label>
          <button type="button" @click="selecionarHoje" :disabled="working || tableWorking">
            Hoje
          </button>
          <button type="button" @click="selecionarUltimosDias(7)" :disabled="working || tableWorking">
            Últimos 7 dias
          </button>
          <button type="button" @click="selecionarUltimosDias(14)" :disabled="working || tableWorking">
            Últimos 14 dias
          </button>
          <button type="button" @click="selecionarUltimosDias(30)" :disabled="working || tableWorking">
            Últimos 30 dias
          </button>
          <button type="button" class="primary" @click="aplicarFiltrosGlobais" :disabled="working || tableWorking">
            Aplicar
          </button>
        </div>
        <div class="download-actions">
          <button type="button" @click="baixarExport('csv')" :class="{ loading: isExporting('csv') }" :disabled="exportWorking">
            <i v-if="isExporting('csv')" class="mini-spinner"></i>
            {{ isExporting('csv') ? 'Baixando CSV' : 'CSV' }}
          </button>
          <button type="button" @click="baixarExport('xls')" :class="{ loading: isExporting('xls') }" :disabled="exportWorking">
            <i v-if="isExporting('xls')" class="mini-spinner"></i>
            {{ isExporting('xls') ? 'Baixando XLS' : 'XLS' }}
          </button>
        </div>
      </section>

      <div class="state error" v-if="exportError">
        <fa :icon="['far', 'triangle-exclamation']" />
        <span>{{ exportError }}</span>
      </div>

      <section class="radar-loader" v-if="initialLoading">
        <div class="loader-header">
          <i></i>
          <span>Carregando radar</span>
        </div>
        <div class="loader-cards">
          <span v-for="item in 6" :key="'loader-card-' + item"></span>
        </div>
        <div class="loader-chart">
          <i v-for="item in 18" :key="'loader-bar-' + item" :style="{ height: loaderBarHeight(item) }"></i>
        </div>
        <div class="loader-table">
          <span v-for="item in 8" :key="'loader-row-' + item"></span>
        </div>
      </section>

      <div class="refresh-loader" v-if="refreshLoading">
        <i class="mini-spinner"></i>
        <span>Atualizando painel</span>
      </div>

      <section class="command-shell" v-if="!initialLoading">
        <section class="signal-grid">
          <button
            type="button"
            v-for="card in cards"
            :key="card.key"
            :class="['metric-card', card.tone, { active: isCardActive(card) }]"
            :aria-pressed="isCardActive(card) ? 'true' : 'false'"
            @click="filtrarTabelaPorCard(card)"
          >
            <i></i>
            <span>{{ card.label }}</span>
            <strong>{{ card.value }}</strong>
            <small>{{ card.hint }}</small>
          </button>
        </section>
      </section>

      <section class="unit-chart" v-if="!initialLoading && unidadeChart.length">
        <header>
          <h2>Unidades</h2>
          <div>
            <span class="success">Contratos assinados</span>
            <span class="info">Aguardando assinatura</span>
            <span class="neutral">Em andamento</span>
            <span class="warning">Parados</span>
            <span class="danger">Bloqueados</span>
            <span class="matricula">MAT</span>
            <span class="rematricula">REMA</span>
          </div>
        </header>

        <div class="unit-table-wrap">
          <table class="unit-summary-table">
            <thead>
              <tr>
                <th>Unidade</th>
                <th class="success">Contratos assinados</th>
                <th class="info">Aguardando assinatura</th>
                <th class="neutral">Em andamento</th>
                <th class="warning">Parados</th>
                <th class="danger">Bloqueados</th>
                <th class="matricula">MAT</th>
                <th class="rematricula">REMA</th>
                <th>Total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in unidadeChart" :key="'unidade-' + row.sigla">
                <th>{{ row.sigla }}</th>
                <td><span class="metric-pill success">{{ formatInteger(row.contratosAssinados) }}</span></td>
                <td><span class="metric-pill info">{{ formatInteger(row.aguardandoAssinatura) }}</span></td>
                <td><span class="metric-pill neutral">{{ formatInteger(row.emAndamentoRestante) }}</span></td>
                <td><span class="metric-pill warning">{{ formatInteger(row.parados) }}</span></td>
                <td><span class="metric-pill danger">{{ formatInteger(row.bloqueados) }}</span></td>
                <td><span class="metric-pill matricula">{{ formatInteger(row.matriculas) }}</span></td>
                <td><span class="metric-pill rematricula">{{ formatInteger(row.rematriculas) }}</span></td>
                <td><strong>{{ formatInteger(row.graficoTotal) }}</strong></td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section class="tracking-table" v-if="!initialLoading">
        <header>
          <h2>Processos</h2>
          <div class="table-filters">
            <label>
              Status
              <select v-model="tableFilters.statusOperacional" @change="aplicarFiltrosTabela">
                <option value="">Todos</option>
                <option v-for="status in statusOptions" :key="status.value" :value="status.value">{{ upper(status.label) }}</option>
              </select>
            </label>
            <label>
              Fluxo
              <select v-model="tableFilters.fluxo" @change="aplicarFiltrosTabela">
                <option value="">Todos</option>
                <option v-for="fluxo in fluxoOptions" :key="fluxo.value" :value="fluxo.value">{{ upper(fluxo.label) }}</option>
              </select>
            </label>
            <label>
              Unidade
              <select v-model="tableFilters.siglaUnidade" @change="aplicarFiltrosTabela">
                <option value="">Todas</option>
                <option v-for="unidade in unidadeOptions" :key="unidade" :value="unidade">{{ unidade }}</option>
              </select>
            </label>
            <label>
              Etapa
              <select v-model="tableFilters.etapaOperacional" @change="aplicarFiltrosTabela">
                <option value="">Todas</option>
                <option v-for="etapa in etapaOptions" :key="etapa.value" :value="etapa.value">{{ upper(etapa.label) }}</option>
              </select>
            </label>
            <label>
              Linhas
              <select v-model.number="tableFilters.perPage" @change="aplicarFiltrosTabela">
                <option v-for="option in tablePerPageOptions" :key="'per-page-' + option" :value="option">{{ option }}</option>
              </select>
            </label>
          </div>
        </header>

        <div class="table-skeleton" v-if="tableWorking && tableRows.length === 0">
          <span v-for="item in 8" :key="'table-loader-' + item"></span>
        </div>

        <div class="table-state error" v-else-if="tableError">{{ tableError }}</div>

        <div class="table-loading-pill" v-if="tableWorking && tableRows.length > 0">Atualizando...</div>

        <div class="table-wrap" v-if="!tableError && (!tableWorking || tableRows.length > 0)">
          <table>
            <thead>
              <tr>
                <th>Última movimentação</th>
                <th>ID matrícula</th>
                <th>Unidade</th>
                <th>Período</th>
                <th>Turma</th>
                <th>Fluxo</th>
                <th>Última etapa</th>
                <th>Aluno</th>
                <th>Responsável</th>
                <th class="actions-col"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in tableRows" :key="tableRowKey(row)" class="clickable-row" @click="abrirDetalhe(row)">
                <td>
                  <strong>{{ formatDateTime(row.ultimaMovimentacao) }}</strong>
                  <span :class="['table-status', statusClass(row.statusOperacional)]">{{ upper(statusLabel(row.statusOperacional)) }}</span>
                </td>
                <td>{{ row.idMatricula || '-' }}</td>
                <td>{{ upper(row.unidade) }}</td>
                <td>{{ upper(row.periodo) }}</td>
                <td>{{ upper(row.turma) }}</td>
                <td>{{ upper(formatFluxo(row.fluxo)) }}</td>
                <td>{{ upper(formatEtapa(row.etapa)) }}</td>
                <td>{{ upper(row.aluno) }}</td>
                <td>{{ upper(row.responsavel) }}</td>
                <td>
                  <button type="button" class="icon-action" @click.stop="abrirDetalhe(row)" :disabled="detailWorking && detailIdMatricula === row.idMatricula">
                    <fa :icon="['fas', 'eye']" />
                  </button>
                </td>
              </tr>
              <tr v-if="tableRows.length === 0">
                <td colspan="10" class="empty">Nenhum processo encontrado para os filtros atuais.</td>
              </tr>
            </tbody>
          </table>
        </div>

        <footer class="table-pagination" v-if="tableMeta.total > 0">
          <span>{{ tablePaginationStart }}-{{ tablePaginationEnd }} de {{ formatInteger(tableMeta.total) }}</span>
          <div>
            <button type="button" @click="goTablePage(1)" :disabled="tableWorking || tableMeta.page <= 1">Primeira</button>
            <button type="button" @click="goTablePage(tableMeta.page - 1)" :disabled="tableWorking || tableMeta.page <= 1">Anterior</button>
            <strong>{{ tableMeta.page }} / {{ tableMeta.lastPage }}</strong>
            <button type="button" @click="goTablePage(tableMeta.page + 1)" :disabled="tableWorking || tableMeta.page >= tableMeta.lastPage">Próxima</button>
            <button type="button" @click="goTablePage(tableMeta.lastPage)" :disabled="tableWorking || tableMeta.page >= tableMeta.lastPage">Última</button>
          </div>
        </footer>
      </section>

    </main>

    <teleport to="body">
      <div class="detail-overlay" v-if="detailOpen" @click.self="fecharDetalhe">
        <aside class="detail-drawer">
          <header>
            <div>
              <span>Detalhe do processo</span>
              <h2>{{ detalheTitulo }}</h2>
              <small v-if="detalheProcesso">MATRÍCULA #{{ detalheProcesso.idMatricula || '-' }}</small>
            </div>
            <button type="button" @click="fecharDetalhe">
              <fa :icon="['far', 'xmark']" />
            </button>
          </header>

          <div class="detail-body">
            <div class="table-state" v-if="detailWorking">Carregando detalhes do processo...</div>

            <div class="table-state error" v-else-if="detailError">{{ detailError }}</div>

            <template v-else-if="detail">
              <section class="detail-summary">
                <div>
                  <span>Status operacional</span>
                  <strong :class="['table-status', statusClass(statusOperacionalDetalhe)]">{{ upper(statusLabel(statusOperacionalDetalhe)) }}</strong>
                </div>
                <div>
                  <span>Última movimentação</span>
                  <strong>{{ formatDateTime(detalheProcesso.createdAt) }}</strong>
                </div>
                <div>
                  <span>Unidade / período / turma</span>
                  <strong>{{ upper(detalheProcesso.siglaUnidade || detalheProcesso.descricaoUnidade) }} · {{ upper(detalheProcesso.descricaoPeriodoLetivo) }} · {{ upper(detalheProcesso.descricaoTurma) }}</strong>
                </div>
                <div>
                  <span>Fluxo / etapa</span>
                  <strong>{{ upper(formatFluxo(detalheProcesso.fluxo)) }} · {{ upper(formatEtapa(detalheProcesso.etapaProcesso || detalheProcesso.etapa)) }}</strong>
                </div>
              </section>

              <section class="detail-section">
                <h3>Aluno</h3>
                <dl>
                  <template v-for="item in alunoDetalhes" :key="'aluno-' + item.label">
                    <dt>{{ item.label }}</dt>
                    <dd>{{ item.value }}</dd>
                  </template>
                </dl>
              </section>

              <section class="detail-section">
                <h3>Responsável principal</h3>
                <dl>
                  <template v-for="item in responsavelPrincipalDetalhes" :key="'resp-' + item.label">
                    <dt>{{ item.label }}</dt>
                    <dd>{{ item.value }}</dd>
                  </template>
                </dl>
              </section>

              <section class="detail-section">
                <h3>Processo e integrações</h3>
                <dl>
                  <template v-for="item in processoDetalhes" :key="'proc-' + item.label">
                    <dt>{{ item.label }}</dt>
                    <dd>{{ item.value }}</dd>
                  </template>
                </dl>
              </section>

              <section class="detail-section">
                <h3>Responsáveis vinculados</h3>
                <div class="detail-list" v-if="detalheResponsaveis.length">
                  <article v-for="responsavel in detalheResponsaveis" :key="'responsavel-' + responsavel.idresponsavel">
                    <strong>{{ upper(responsavel.nome) }}</strong>
                    <small>{{ responsavelFlags(responsavel) }}</small>
                    <span>{{ formatDocumento(responsavel.cpf) }} · {{ formatTelefone(responsavel.telefonecelular || responsavel.telefonefixo) }} · {{ upper(responsavel.email) }}</span>
                    <em>{{ upper(formatEndereco(responsavel)) }}</em>
                  </article>
                </div>
                <p class="detail-empty" v-else>Nenhum responsável vinculado retornado.</p>
              </section>

              <section class="detail-section">
                <h3>Acessos</h3>
                <dl>
                  <template v-for="item in acessoDetalhes" :key="'acesso-' + item.label">
                    <dt>{{ item.label }}</dt>
                    <dd>{{ item.value }}</dd>
                  </template>
                </dl>
              </section>

              <section class="detail-section" v-if="detalheBloqueios.length">
                <h3>Bloqueios ativos</h3>
                <div class="detail-list danger-list">
                  <article v-for="bloqueio in detalheBloqueios" :key="'bloqueio-' + bloqueio.idbloqueioMatricula">
                    <strong>{{ upper(bloqueio.descricao || 'Bloqueio de matrícula') }}</strong>
                    <small>{{ formatDateTime(bloqueio.data) }}</small>
                    <span>{{ upper(bloqueio.mensagem) }}</span>
                  </article>
                </div>
              </section>

              <section class="detail-section">
                <h3>Timeline por etapas</h3>
                <ol class="stage-timeline" v-if="detalheEtapas.length">
                  <li v-for="etapa in detalheEtapas" :key="'etapa-' + etapa.etapa">
                    <i :class="statusClass(etapa.status)"></i>
                    <div>
                      <strong>{{ upper(formatEtapa(etapa.etapa)) }}</strong>
                      <small>{{ formatDateTime(etapa.primeiroAcesso) }} até {{ formatDateTime(etapa.ultimoAcesso) }} · {{ formatInteger(etapa.totalEventos) }} evento(s) · {{ formatMinutes(etapa.duracaoMinutos) }}</small>
                      <p v-if="etapa.erro">{{ upper(etapa.erro) }}</p>
                      <span v-for="acao in etapa.eventosTecnicos" :key="'acao-etapa-' + etapa.etapa + '-' + acao">{{ upper(formatEtapa(acao)) }}</span>
                    </div>
                  </li>
                </ol>
                <p class="detail-empty" v-else>Nenhuma etapa retornada.</p>
              </section>

              <section class="detail-section">
                <h3>Eventos técnicos</h3>
                <ol class="event-timeline" v-if="detalheTimeline.length">
                  <li v-for="evento in detalheTimeline" :key="'evento-' + evento.idLogMatricula">
                    <time>{{ formatDateTime(evento.createdAt) }}</time>
                    <div>
                      <strong>{{ upper(formatEtapa(evento.acao || evento.status || evento.etapa)) }}</strong>
                      <small>{{ upper(formatEtapa(evento.etapa)) }} · {{ upper(evento.status) }} · {{ upper(evento.origem) }}</small>
                      <p v-if="evento.erro">{{ upper(evento.erro) }}</p>
                      <em v-if="evento.tempoDesdeAnteriorMinutos !== null">+{{ formatMinutes(evento.tempoDesdeAnteriorMinutos) }} desde o anterior</em>
                    </div>
                  </li>
                </ol>
                <p class="detail-empty" v-else>Nenhum evento retornado.</p>
              </section>
            </template>
          </div>
        </aside>
      </div>
    </teleport>
  </controller>
</template>

<script>
import MatriculaTrackingService from '@/services/matricula-tracking.service.js'

export default {
  name: 'MatriculaTracking',
  data () {
    return {
      working: false,
      tableWorking: false,
      exportWorking: false,
      exportFormat: null,
      firstLoadDone: false,
      error: null,
      tableError: null,
      exportError: null,
      filters: this.defaultFilters(),
      tableFilters: this.defaultTableFilters(),
      tableRows: [],
      tableMeta: this.defaultTableMeta(),
      dashboard: this.emptyDashboard(),
      detailOpen: false,
      detailWorking: false,
      detailError: null,
      detail: null,
      detailIdMatricula: null
    }
  },
  computed: {
    initialLoading () {
      return !this.firstLoadDone && (this.working || this.tableWorking)
    },
    minTrackingDate () {
      return '2026-06-26'
    },
    refreshLoading () {
      return this.firstLoadDone && (this.working || this.tableWorking)
    },
    cards () {
      const cards = this.dashboard.cards || {}

      return [
        { key: 'contratosAssinados', status: 'contratos_assinados', label: 'Contratos assinados', value: this.formatInteger(cards.contratosAssinados), hint: 'situação 1 + contrato assinado', tone: 'success' },
        { key: 'assinatura', status: 'aguardando_assinatura', label: 'Aguardando assinatura', value: this.formatInteger(cards.aguardandoAssinatura), hint: 'envelope sem conclusão', tone: 'info' },
        { key: 'emAndamento', status: 'em_andamento', label: 'Em andamento', value: this.formatInteger(cards.emAndamento), hint: 'não finalizados', tone: 'neutral' },
        { key: 'parados', status: 'parados', label: 'Parados', value: this.formatInteger(cards.parados), hint: `sem avanço há ${this.filters.horas}h+`, tone: 'warning' },
        { key: 'bloqueio', status: 'bloqueados', label: 'Bloqueados', value: this.formatInteger(cards.comBloqueio), hint: 'bloqueio de matrícula ativo', tone: 'danger' },
        { key: 'erros', status: 'com_erro', label: 'Com erro', value: this.formatInteger(cards.erros), hint: 'erro ainda não superado', tone: 'danger' }
      ]
    },
    unidadeChart () {
      return (this.dashboard.porUnidade || [])
        .map(row => {
          const contratosAssinados = Number(row.contratosAssinados || row.finalizados || 0)
          const aguardandoAssinatura = Number(row.aguardandoAssinatura || 0)
          const emAndamento = Number(row.emAndamento || 0)
          const parados = Number(row.parados || 0)
          const bloqueados = Number(row.bloqueados || 0)
          const matriculas = Number(row.matriculas || 0)
          const rematriculas = Number(row.rematriculas || 0)
          const automatriculas = Number(row.automatriculas || 0)
          const fluxoTotal = Math.max(1, matriculas + rematriculas)
          const emAndamentoRestante = row.emAndamentoRestante !== undefined && row.emAndamentoRestante !== null
            ? Number(row.emAndamentoRestante || 0)
            : Math.max(0, emAndamento - aguardandoAssinatura - parados)
          const graficoTotal = contratosAssinados + aguardandoAssinatura + emAndamentoRestante + parados + bloqueados

          return {
            sigla: row.sigla || 'S/U',
            contratosAssinados,
            finalizados: contratosAssinados,
            aguardandoAssinatura,
            emAndamento,
            emAndamentoRestante,
            parados,
            bloqueados,
            matriculas,
            rematriculas,
            automatriculas,
            fluxoTotal,
            graficoTotal,
            total: Number(row.total || graficoTotal)
          }
        })
        .filter(row => row.total > 0)
    },
    unidadeOptions () {
      return this.unidadeChart
        .map(row => row.sigla)
        .filter(Boolean)
    },
    statusOptions () {
      return [
        { value: 'contratos_assinados', label: 'Contratos assinados' },
        { value: 'aguardando_assinatura', label: 'Aguardando assinatura' },
        { value: 'em_andamento', label: 'Em andamento' },
        { value: 'parados', label: 'Parados' },
        { value: 'bloqueados', label: 'Bloqueados' },
        { value: 'com_erro', label: 'Com erro' }
      ]
    },
    fluxoOptions () {
      return [
        { value: 'matricula', label: 'Mat' },
        { value: 'rematricula', label: 'Remat' },
        { value: 'automatricula', label: 'Auto' }
      ]
    },
    etapaOptions () {
      return [
        { value: 'tela_aluno', label: 'Tela aluno' },
        { value: 'tela_responsaveis', label: 'Tela responsáveis' },
        { value: 'tela_documentos', label: 'Tela documentos' },
        { value: 'tela_planos_pagamento', label: 'Tela planos de pagamento' },
        { value: 'plano_selecionado', label: 'Plano selecionado' },
        { value: 'tela_contrato', label: 'Tela contrato' },
        { value: 'contrato_enviado_assinatura', label: 'Contrato enviado para assinatura' },
        { value: 'assinatura_contrato_aberta', label: 'Assinatura do contrato aberta' },
        { value: 'contrato_assinado', label: 'Contrato assinado' },
        { value: 'matricula_finalizada', label: 'Matrícula finalizada' },
        { value: 'sem_etapa', label: 'Sem etapa' }
      ]
    },
    tablePerPageOptions () {
      return [20, 50, 100]
    },
    tablePaginationStart () {
      if (!this.tableMeta.total) return 0

      return ((this.tableMeta.page - 1) * this.tableMeta.perPage) + 1
    },
    tablePaginationEnd () {
      return Math.min(this.tableMeta.page * this.tableMeta.perPage, this.tableMeta.total)
    },
    detalheProcesso () {
      return (this.detail && this.detail.processo) || {}
    },
    detalheTimeline () {
      return (this.detail && this.detail.timeline) || []
    },
    detalheEtapas () {
      return (this.detail && this.detail.timelineEtapas) || []
    },
    detalheResponsaveis () {
      return (this.detail && this.detail.responsaveis) || []
    },
    detalheBloqueios () {
      return (this.detail && this.detail.bloqueios) || []
    },
    detalheAcessos () {
      return (this.detail && this.detail.acessos) || {}
    },
    detalheTitulo () {
      return this.upper(this.detalheProcesso.nomeAluno || 'Matrícula')
    },
    statusOperacionalDetalhe () {
      const processo = this.detalheProcesso

      if (Number(processo.erroAtivo || 0) === 1) return 'com_erro'
      if (Number(processo.totalBloqueios || 0) > 0) return 'bloqueados'
      if (Number(processo.temContratoAssinado || 0) === 1) return 'contratos_assinados'
      if (Number(processo.processoFinalizado || 0) === 1 || Number(processo.idSituacao || 0) === 1) return 'finalizados'
      if (Number(processo.temAguardandoAssinatura || 0) === 1 || Number(processo.temContratoPendente || 0) === 1) return 'aguardando_assinatura'

      return 'em_andamento'
    },
    alunoDetalhes () {
      const processo = this.detalheProcesso

      return [
        this.detailField('Nome', processo.nomeAluno),
        this.detailField('CPF', this.formatDocumento(processo.cpfAluno)),
        this.detailField('Nascimento', this.formatDateOnly(processo.dataNascimentoAluno)),
        this.detailField('E-mail', processo.emailAluno),
        this.detailField('Celular', this.formatTelefone(processo.telefoneAluno)),
        this.detailField('Telefone fixo', this.formatTelefone(processo.telefoneFixoAluno)),
        this.detailField('Endereço', this.formatEndereco({
          logradouro: processo.logradouroAluno,
          numero: processo.numeroAluno,
          complemento: processo.complementoAluno,
          bairro: processo.bairroAluno,
          cep: processo.cepAluno
        }))
      ]
    },
    responsavelPrincipalDetalhes () {
      const processo = this.detalheProcesso

      return [
        this.detailField('Nome', processo.nomeResponsavel),
        this.detailField('CPF', this.formatDocumento(processo.cpfResponsavel)),
        this.detailField('E-mail', processo.emailResponsavel),
        this.detailField('Celular', this.formatTelefone(processo.telefoneResponsavel)),
        this.detailField('Telefone fixo', this.formatTelefone(processo.telefoneFixoResponsavel)),
        this.detailField('Endereço', this.formatEndereco({
          logradouro: processo.logradouroResponsavel,
          numero: processo.numeroResponsavel,
          complemento: processo.complementoResponsavel,
          bairro: processo.bairroResponsavel,
          cep: processo.cepResponsavel
        }))
      ]
    },
    processoDetalhes () {
      const processo = this.detalheProcesso

      return [
        this.detailField('ID matrícula', processo.idMatricula),
        this.detailField('Código matrícula', processo.matcodigo),
        this.detailField('ID aluno', processo.idAlunoAtual || processo.idAluno),
        this.detailField('Situação', this.formatSituacao(processo)),
        this.detailField('Data matrícula', this.formatDateTime(processo.dataMatricula)),
        this.detailField('Data situação', this.formatDateTime(processo.dataSituacao)),
        this.detailField('Unidade', processo.descricaoUnidade || processo.siglaUnidade),
        this.detailField('Série', processo.descricaoSerie),
        this.detailField('Período letivo', processo.descricaoPeriodoLetivo),
        this.detailField('Turma', processo.descricaoTurma),
        this.detailField('Envelope', processo.envelopeIdAtual || processo.envelopeId),
        this.detailField('Contrato criado em', this.formatDateTime(processo.contratoCriadoAt)),
        this.detailField('Último contrato', this.formatDateTime(processo.ultimoContratoAt)),
        this.detailField('Última finalização', this.formatDateTime(processo.ultimoFinalizadoAt)),
        this.detailField('Executor', processo.nomeExecutorFluxo || processo.nomeUsuarioFluxo),
        this.detailField('E-mail executor', processo.emailExecutorFluxo || processo.emailUsuarioFluxo),
        this.detailField('Tipo executor', processo.tipoExecutorFluxo),
        this.detailField('Origem', processo.origem),
        this.detailField('Rota', processo.rota),
        this.detailField('Sessão', processo.sessionId),
        this.detailField('IP', processo.ip),
        this.detailField('Erro ativo', processo.erroResumo || processo.ultimoErro || processo.erro),
        this.detailField('Metadata', this.formatJson(processo.metadata))
      ]
    },
    acessoDetalhes () {
      const acessos = this.detalheAcessos

      return [
        this.detailField('Primeiro acesso', this.formatDateTime(acessos.primeiroAcesso)),
        this.detailField('Último acesso', this.formatDateTime(acessos.ultimoAcesso)),
        this.detailField('Eventos', this.formatInteger(acessos.totalEventos)),
        this.detailField('Etapas', this.formatInteger(acessos.totalEtapas)),
        this.detailField('Origens', this.formatList(acessos.origens)),
        this.detailField('Rotas', this.formatList(acessos.rotas))
      ]
    }
  },
  mounted () {
    this.carregar()
    document.addEventListener('keydown', this.onDetailKeydown)
  },
  beforeUnmount () {
    this.unlockDetailView()
    document.removeEventListener('keydown', this.onDetailKeydown)
  },
  methods: {
    defaultFilters () {
      return {
        horas: '24',
        dataInicio: '2026-06-26',
        dataFim: '',
        limit: 500
      }
    },
    defaultTableFilters () {
      return {
        statusOperacional: '',
        fluxo: '',
        siglaUnidade: '',
        etapaOperacional: '',
        page: 1,
        perPage: 20
      }
    },
    defaultTableMeta () {
      return {
        page: 1,
        perPage: 20,
        total: 0,
        lastPage: 1
      }
    },
    emptyDashboard () {
      return {
        cards: {},
        porUnidade: []
      }
    },
    todayIso () {
      const today = new Date()

      return this.dateToIso(today)
    },
    dateToIso ( date ) {
      const year = date.getFullYear()
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const day = String(date.getDate()).padStart(2, '0')

      return `${year}-${month}-${day}`
    },
    normalizarPeriodo () {
      if (!this.filters.dataInicio || this.filters.dataInicio < this.minTrackingDate) {
        this.filters.dataInicio = this.minTrackingDate
      }

      if (this.filters.dataFim && this.filters.dataFim < this.minTrackingDate) {
        this.filters.dataFim = this.minTrackingDate
      }

      if (this.filters.dataFim && this.filters.dataInicio > this.filters.dataFim) {
        this.filters.dataFim = this.filters.dataInicio
      }
    },
    selecionarHoje () {
      const hoje = this.todayIso()
      this.filters.dataInicio = hoje < this.minTrackingDate ? this.minTrackingDate : hoje
      this.filters.dataFim = this.filters.dataInicio

      return this.aplicarFiltrosGlobais()
    },
    selecionarUltimosDias ( dias ) {
      const hoje = new Date()
      const inicio = new Date(hoje)
      inicio.setDate(hoje.getDate() - Math.max(1, Number(dias || 1)) + 1)

      const dataInicio = this.dateToIso(inicio)
      const dataFim = this.dateToIso(hoje)

      this.filters.dataInicio = dataInicio < this.minTrackingDate ? this.minTrackingDate : dataInicio
      this.filters.dataFim = dataFim < this.minTrackingDate ? this.minTrackingDate : dataFim

      return this.aplicarFiltrosGlobais()
    },
    params () {
      this.normalizarPeriodo()

      return Object.keys(this.filters).reduce((params, key) => {
        if (this.filters[key] !== '' && this.filters[key] !== null) {
          params[key] = this.filters[key]
        }

        return params
      }, {})
    },
    carregar () {
      if (this.working) {
        return null
      }

      this.working = true
      this.error = null

      return MatriculaTrackingService.dashboard(this.params())
        .then(response => {
          this.dashboard = {
            ...this.emptyDashboard(),
            ...(response.data.data || {})
          }

          return this.carregarTabela()
        })
        .catch(error => {
          this.error = this.mensagemErro(error)
        })
        .finally(() => {
          this.working = false
          this.firstLoadDone = true
        })
    },
    carregarTabela () {
      this.tableWorking = true
      this.tableError = null

      return MatriculaTrackingService.table({
        ...this.params(),
        ...this.tableFilters
      })
        .then(response => {
          this.tableRows = response.data.data || []
          this.tableMeta = {
            ...this.defaultTableMeta(),
            ...(response.data.meta || {})
          }
        })
        .catch(error => {
          this.tableError = this.mensagemErro(error)
        })
        .finally(() => {
          this.tableWorking = false
        })
    },
    aplicarFiltrosTabela () {
      this.tableFilters.page = 1

      return this.carregarTabela()
    },
    aplicarFiltrosGlobais () {
      this.normalizarPeriodo()
      this.tableFilters.page = 1

      return this.carregar()
    },
    baixarExport ( format ) {
      if (this.exportWorking) {
        return null
      }

      this.exportWorking = true
      this.exportFormat = format
      this.exportError = null

      const params = {
        ...this.params(),
        statusOperacional: this.tableFilters.statusOperacional,
        fluxo: this.tableFilters.fluxo,
        siglaUnidade: this.tableFilters.siglaUnidade,
        etapaOperacional: this.tableFilters.etapaOperacional,
        format
      }

      return MatriculaTrackingService.download(params)
        .then(response => {
          const blob = new Blob([response.data], {
            type: response.headers['content-type'] || 'application/octet-stream'
          })
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')

          link.href = url
          link.download = this.exportFilename(response, format)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
        })
        .catch(error => {
          this.exportError = this.mensagemErro(error)
        })
        .finally(() => {
          this.exportWorking = false
          this.exportFormat = null
        })
    },
    isExporting ( format ) {
      return this.exportWorking && this.exportFormat === format
    },
    exportFilename ( response, format ) {
      const disposition = response.headers['content-disposition'] || ''
      const match = disposition.match(/filename="?([^"]+)"?/i)

      if (match && match[1]) {
        return match[1]
      }

      return `radar-matriculas.${format === 'xls' ? 'xls' : 'csv'}`
    },
    loaderBarHeight ( value ) {
      const heights = [42, 64, 35, 78, 52, 90, 44, 70, 58, 82, 38, 66, 48, 74, 56, 88, 46, 62]

      return `${heights[(Number(value) - 1) % heights.length]}%`
    },
    filtrarTabelaPorCard ( card ) {
      if (!card || !card.status || this.tableWorking) {
        return null
      }

      this.tableFilters.statusOperacional = card.status
      this.tableFilters.page = 1

      return this.carregarTabela()
    },
    isCardActive ( card ) {
      return !!card && this.tableFilters.statusOperacional === card.status
    },
    goTablePage ( page ) {
      const nextPage = Math.max(1, Math.min(Number(page || 1), this.tableMeta.lastPage || 1))

      if (nextPage === this.tableMeta.page || this.tableWorking) {
        return null
      }

      this.tableFilters.page = nextPage

      return this.carregarTabela()
    },
    abrirDetalhe ( row ) {
      if (!row || !row.idMatricula || this.detailWorking) {
        return null
      }

      this.lockDetailView()
      this.detailOpen = true
      this.detailWorking = true
      this.detailError = null
      this.detail = null
      this.detailIdMatricula = row.idMatricula

      return MatriculaTrackingService.detail(row.idMatricula)
        .then(response => {
          this.detail = response.data.data || null
        })
        .catch(error => {
          this.detailError = this.mensagemErro(error)
        })
        .finally(() => {
          this.detailWorking = false
        })
    },
    fecharDetalhe () {
      this.unlockDetailView()
      this.detailOpen = false
      this.detailWorking = false
      this.detailError = null
      this.detail = null
      this.detailIdMatricula = null
    },
    onDetailKeydown ( event ) {
      if (event.key === 'Escape' && this.detailOpen) {
        this.fecharDetalhe()
      }
    },
    detailField ( label, value ) {
      return {
        label,
        value: value === null || value === undefined || value === '' ? '-' : value
      }
    },
    lockDetailView () {
      if (typeof document === 'undefined') return

      document.body.classList.add('tracking-detail-open')
    },
    unlockDetailView () {
      if (typeof document === 'undefined') return

      document.body.classList.remove('tracking-detail-open')
    },
    formatInteger ( value ) {
      const number = Number(value || 0)

      return Number.isNaN(number) ? '0' : number.toLocaleString('pt-BR')
    },
    formatDateOnly ( value ) {
      if (!value) return '-'

      const date = new Date(String(value).match(/^\d{4}-\d{2}-\d{2}$/) ? `${value}T00:00:00` : value)
      if (Number.isNaN(date.getTime())) return '-'

      return date.toLocaleDateString('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
    },
    formatDateTime ( value ) {
      if (!value) return '-'

      const date = this.parseTrackingDateTime(value)
      if (Number.isNaN(date.getTime())) return '-'

      return date.toLocaleString('pt-BR', {
        timeZone: 'America/Sao_Paulo',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },
    parseTrackingDateTime ( value ) {
      const rawValue = String(value).trim()
      const normalizedValue = rawValue.replace(/\.(\d{3})\d+(Z|[+-]\d{2}:?\d{2})$/i, '.$1$2')
      const hasExplicitTimezone = /(?:Z|[+-]\d{2}:?\d{2})$/i.test(normalizedValue)
      const dateTime = normalizedValue.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/)

      if (dateTime && !hasExplicitTimezone) {
        const [, year, month, day, hour, minute, second = '00'] = dateTime

        return new Date(`${year}-${month}-${day}T${hour}:${minute}:${second}Z`)
      }

      return new Date(normalizedValue)
    },
    formatDocumento ( value ) {
      const digits = String(value || '').replace(/\D+/g, '')
      if (!digits) return '-'

      if (digits.length === 11) {
        return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`
      }

      return value
    },
    formatTelefone ( value ) {
      const digits = String(value || '').replace(/\D+/g, '')
      if (!digits) return '-'

      if (digits.length === 11) {
        return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`
      }

      if (digits.length === 10) {
        return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`
      }

      return value
    },
    formatEndereco ( value ) {
      const partes = [
        value?.logradouro,
        value?.numero,
        value?.complemento,
        value?.bairro,
        value?.cep ? `CEP ${this.formatCep(value.cep)}` : null
      ].filter(Boolean)

      return partes.length ? partes.join(', ') : '-'
    },
    formatCep ( value ) {
      const digits = String(value || '').replace(/\D+/g, '')
      if (digits.length !== 8) return value || '-'

      return `${digits.slice(0, 5)}-${digits.slice(5)}`
    },
    formatMinutes ( value ) {
      const minutes = Number(value)
      if (Number.isNaN(minutes) || minutes < 0) return '-'

      if (minutes < 60) return `${Math.round(minutes)}min`

      const hours = Math.floor(minutes / 60)
      const restMinutes = Math.round(minutes % 60)
      if (hours < 24) return restMinutes ? `${hours}h ${restMinutes}min` : `${hours}h`

      const days = Math.floor(hours / 24)
      const restHours = hours % 24

      return restHours ? `${days}d ${restHours}h` : `${days}d`
    },
    formatList ( value ) {
      if (!Array.isArray(value) || value.length === 0) return '-'

      return value.map(item => this.upper(item)).join(', ')
    },
    formatJson ( value ) {
      if (!value) return '-'

      if (typeof value === 'string') {
        return value
      }

      try {
        return JSON.stringify(value)
      } catch (error) {
        return '-'
      }
    },
    formatSituacao ( processo ) {
      const descricao = processo?.descricaoSituacao || null
      const sigla = processo?.siglaSituacao || null

      if (descricao && sigla) return `${descricao} (${sigla})`

      return descricao || sigla || processo?.idSituacao || '-'
    },
    responsavelFlags ( responsavel ) {
      const flags = []

      if (Number(responsavel.financeiro || 0) === 1) flags.push('FINANCEIRO')
      if (Number(responsavel.pedagogico || 0) === 1) flags.push('PEDAGÓGICO')
      if (responsavel.diavencimento) flags.push(`VENC. DIA ${responsavel.diavencimento}`)

      return flags.length ? flags.join(' · ') : 'RESPONSÁVEL'
    },
    statusLabel ( value ) {
      const option = this.statusOptions.find(status => status.value === value)
      if (option) return option.label

      return {
        finalizados: 'Finalizados',
        concluido: 'Concluído',
        concluida: 'Concluída',
        success: 'Concluído',
        erro: 'Com erro',
        erro_validacao: 'Erro de validação'
      }[value] || 'Sem status'
    },
    formatFluxo ( value ) {
      return {
        matricula: 'Mat',
        rematricula: 'Rema',
        automatricula: 'Auto'
      }[value] || value || '-'
    },
    formatEtapa ( value ) {
      if (!value) return '-'

      const labels = {
        tela_aluno: 'Tela aluno',
        tela_responsaveis: 'Tela responsáveis',
        tela_documentos: 'Tela documentos',
        tela_planos_pagamento: 'Tela planos de pagamento',
        plano_selecionado: 'Plano selecionado',
        tela_contrato: 'Tela contrato',
        contrato_enviado_assinatura: 'Contrato enviado para assinatura',
        assinatura_contrato_aberta: 'Assinatura do contrato aberta',
        contrato_assinado: 'Contrato assinado',
        matricula_finalizada: 'Matrícula finalizada',
        sem_etapa: 'Sem etapa'
      }

      if (labels[value]) return labels[value]

      return String(value).replace(/_/g, ' ').replace(/-/g, ' ')
    },
    upper ( value ) {
      if (value === null || value === undefined || value === '') return '-'

      return String(value).toLocaleUpperCase('pt-BR')
    },
    statusClass ( value ) {
      return {
        finalizados: 'success',
        concluido: 'success',
        concluida: 'success',
        success: 'success',
        contratos_assinados: 'success',
        aguardando_assinatura: 'info',
        em_andamento: 'neutral',
        parados: 'warning',
        bloqueados: 'danger',
        com_erro: 'danger',
        erro: 'danger',
        erro_validacao: 'danger'
      }[value] || 'neutral'
    },
    tableRowKey ( row ) {
      return `${row.idMatricula || 'sem-matricula'}-${row.ultimaMovimentacao || ''}`
    },
    mensagemErro ( error ) {
      const response = error?.response

      if (!response) {
        return 'Não foi possível conectar na API de tracking. Verifique se a API local está rodando.'
      }

      const data = response.data || {}
      const message = data.message || data.error || null

      if (response.status === 401) {
        return 'Sessão expirada ou token ausente. Faça login novamente para carregar o tracking de matrículas.'
      }

      if (response.status === 403) {
        return 'Seu usuário não tem permissão para carregar o tracking de matrículas.'
      }

      if (response.status >= 500) {
        return message || 'Erro na API ao carregar o tracking. Verifique se a tabela logMatriculas existe no banco usado localmente.'
      }

      return message || `Não foi possível carregar o tracking de matrículas. Código ${response.status}.`
    }
  }
}
</script>

<style lang="scss" scoped>
  .tracking-matriculas {
    width: 100%;
    max-width: 100%;
    overflow-x: hidden;
    box-sizing: border-box;
    padding: $hmg_mid $hmg_mid $mg_extra;

    *,
    *:before,
    *:after {
      box-sizing: border-box;
    }

    > * {
      max-width: 100%;
      min-width: 0;
    }

    .state {
      display: flex;
      align-items: center;
      gap: $hmg_mini;
      border-radius: 6px;
      padding: $hmg_mini $hmg_mid;
      margin-bottom: $mg;

      &.error {
        background: #fff1f1;
        color: #9d1c1c;
      }
    }
  }

  .mini-spinner {
    display: inline-block;
    width: 13px;
    height: 13px;
    border: 2px solid rgba(50, 104, 121, .22);
    border-top-color: #326879;
    border-radius: 50%;
    animation: trackingSpin .75s linear infinite;
  }

  .refresh-loader {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    width: max-content;
    border: 1px solid #d8e5ee;
    border-radius: 999px;
    background: #ffffff;
    color: #526b7b;
    font-size: $font-size-mini;
    font-weight: 900;
    margin-bottom: $hmg_mid;
    padding: 7px 11px;
    text-transform: uppercase;
  }

  .radar-loader {
    display: grid;
    gap: $hmg_mid;
    border: 1px solid #d8e5ee;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 10px 24px rgba(24, 42, 58, .05);
    padding: $hmg_mid;
  }

  .loader-header {
    display: flex;
    align-items: center;
    gap: 9px;
    color: #526b7b;
    font-size: $font-size-mini;
    font-weight: 900;
    text-transform: uppercase;

    i {
      @extend .mini-spinner;
    }
  }

  .loader-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(180px, 100%), 1fr));
    gap: $hmg_mid;

    span {
      min-height: 118px;
      border-radius: 8px;
      background: linear-gradient(100deg, #edf3f7 0%, #f8fbfd 48%, #edf3f7 100%);
      background-size: 220% 100%;
      animation: trackingShimmer 1.15s ease-in-out infinite;
    }
  }

  .loader-chart {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(28px, 1fr));
    align-items: end;
    gap: 7px;
    min-height: clamp(120px, 18vh, 230px);
    padding-top: $hmg_mid;

    i {
      width: min(28px, 72%);
      justify-self: center;
      border-radius: 7px 7px 0 0;
      background: linear-gradient(180deg, #dce8f0, #edf3f7);
      animation: trackingPulse 1.2s ease-in-out infinite;
    }
  }

  .loader-table,
  .table-skeleton {
    display: grid;
    gap: 8px;
    border: 1px solid #d8e5ee;
    border-radius: 8px;
    background: #ffffff;
    padding: $hmg_mid;

    span {
      height: 38px;
      border-radius: 6px;
      background: linear-gradient(100deg, #edf3f7 0%, #f8fbfd 48%, #edf3f7 100%);
      background-size: 220% 100%;
      animation: trackingShimmer 1.15s ease-in-out infinite;
    }
  }

  .global-filters {
    display: flex;
    align-items: flex-end;
    justify-content: space-between;
    gap: $hmg_mid;
    margin-bottom: $hmg_mid;

    > div {
      display: flex;
      align-items: flex-end;
      gap: $hmg_mid;
      flex-wrap: wrap;
    }

    label {
      display: grid;
      gap: 5px;
      color: #526b7b;
      font-size: $font-size-mini;
      font-weight: 900;
      text-transform: uppercase;
    }

    input {
      min-height: 34px;
      border: 1px solid #d8e5ee;
      border-radius: 6px;
      background: #ffffff;
      color: #203847;
      font-weight: 800;
      padding: 0 9px;
    }

    .date-range-field {
      min-width: min(100%, 315px);
    }

    .date-range-control {
      display: inline-grid;
      grid-template-columns: minmax(0, 1fr) 18px minmax(0, 1fr);
      align-items: center;
      min-height: 34px;
      border: 1px solid #d8e5ee;
      border-radius: 6px;
      background: #ffffff;
      overflow: hidden;

      input {
        min-width: 0;
        border: 0;
        border-radius: 0;
        background: transparent;

        &:focus {
          outline: 0;
          box-shadow: inset 0 -2px 0 #54bed0;
        }
      }

      i {
        display: block;
        height: 1px;
        background: #7c9aab;
      }
    }

    button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      gap: 7px;
      min-height: 34px;
      border: 1px solid #d8e5ee;
      border-radius: 6px;
      background: #ffffff;
      color: #326879;
      cursor: pointer;
      font-size: $font-size-mini;
      font-weight: 900;
      padding: 0 $hmg_mid;
      text-transform: uppercase;

      &:hover,
      &:focus {
        border-color: #54bed0;
        color: #123744;
        outline: 0;
      }

      &:disabled {
        cursor: not-allowed;
        opacity: .55;
      }

      &.primary {
        border-color: #54bed0;
        background: #e0f7fb;
        color: #123744;
      }

      &.loading {
        min-width: 116px;
      }
    }
  }

  .download-actions {
    justify-content: flex-end;
  }

  @keyframes trackingSpin {
    to { transform: rotate(360deg); }
  }

  @keyframes trackingShimmer {
    0% { background-position: 120% 0; }
    100% { background-position: -120% 0; }
  }

  @keyframes trackingPulse {
    0%, 100% { opacity: .55; }
    50% { opacity: 1; }
  }

  .command-shell {
    position: relative;
    padding: 0;

    &:before {
      content: none;
    }

    > * {
      position: relative;
      z-index: 1;
    }
  }

  .signal-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(min(180px, 100%), 1fr));
    gap: $hmg_mid;
    max-width: 100%;
    min-width: 0;

    .metric-card {
      --metric-accent: #54bed0;
      --metric-bg-start: #e8f8fb;
      --metric-bg-end: #d5f1f6;
      --metric-border: #a7dfe9;
      --metric-text: #173244;
      --metric-muted: #426575;
      position: relative;
      overflow: hidden;
      appearance: none;
      min-height: 118px;
      border: 1px solid var(--metric-border);
      border-radius: 8px;
      background:
        linear-gradient(135deg, var(--metric-bg-start), var(--metric-bg-end)),
        linear-gradient(180deg, rgba(255, 255, 255, .22), transparent);
      box-shadow: 0 10px 24px rgba(24, 42, 58, .08);
      cursor: pointer;
      font: inherit;
      padding: $hmg_mid;
      text-align: left;
      width: 100%;

      &:hover,
      &:focus {
        box-shadow: 0 14px 30px rgba(24, 42, 58, .12);
      }

      &:focus {
        outline: 0;
      }

      &.active {
        border-color: var(--metric-accent);
        box-shadow: 0 14px 32px rgba(24, 42, 58, .14), inset 0 0 0 2px rgba(255, 255, 255, .52);
      }

      &:before {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(135deg, rgba(255, 255, 255, .22), transparent 44%);
        pointer-events: none;
      }

      i {
        display: block;
        width: 26px;
        height: 3px;
        border-radius: 999px;
        background: var(--metric-accent);
        margin-bottom: $hmg_mini;
      }

      span,
      small {
        position: relative;
        display: block;
        color: var(--metric-muted);
      }

      span {
        font-size: $font-size-mini;
        font-weight: 800;
        text-transform: uppercase;
      }

      strong {
        position: relative;
        display: block;
        margin: 8px 0 4px;
        color: var(--metric-text);
        font-size: 34px;
        line-height: 1;
      }

      &.success {
        --metric-accent: #3fd49b;
        --metric-bg-start: #ddf8ed;
        --metric-bg-end: #c7f1df;
        --metric-border: #96dec0;
        --metric-text: #103a2a;
        --metric-muted: #2e6d55;
      }

      &.info {
        --metric-accent: #54bed0;
        --metric-bg-start: #e0f7fb;
        --metric-bg-end: #caedf4;
        --metric-border: #98d8e5;
        --metric-text: #123744;
        --metric-muted: #326879;
      }

      &.neutral {
        --metric-accent: #8fa6b8;
        --metric-bg-start: #edf3f7;
        --metric-bg-end: #dde8ef;
        --metric-border: #bdceda;
        --metric-text: #203847;
        --metric-muted: #526b7b;
      }

      &.warning {
        --metric-accent: #e7bf4f;
        --metric-bg-start: #fff6d8;
        --metric-bg-end: #f7e6a8;
        --metric-border: #e2c967;
        --metric-text: #503d08;
        --metric-muted: #746122;
      }

      &.danger {
        --metric-accent: #df6c8b;
        --metric-bg-start: #fde5ec;
        --metric-bg-end: #f6cbd7;
        --metric-border: #e7a3b5;
        --metric-text: #552133;
        --metric-muted: #7c4355;
      }
    }
  }

  .unit-chart {
    margin-top: $mg;
    max-width: 100%;
    min-width: 0;
    overflow: hidden;

    > header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: $hmg_mid;
      margin-bottom: $hmg_mid;

      h2 {
        margin: 0;
        color: #1f3340;
        font-size: 18px;
        line-height: 1.2;
      }

      div {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: $hmg_mid;
        flex-wrap: wrap;
      }

      span {
        display: inline-flex;
        align-items: center;
        gap: 7px;
        color: #526b7b;
        font-size: $font-size-mini;
        font-weight: 800;
        text-transform: uppercase;

        &:before {
          content: '';
          width: 10px;
          height: 10px;
          border-radius: 50%;
          background: #8fa6b8;
        }

        &.success:before { background: #3fd49b; }
        &.info:before { background: #54bed0; }
        &.neutral:before { background: #8fa6b8; }
        &.warning:before { background: #e7bf4f; }
        &.danger:before { background: #df6c8b; }
        &.matricula:before { background: #17a2b8; }
        &.rematricula:before { background: #7c5cff; }
      }
    }
  }

  .unit-table-wrap {
    max-width: 100%;
    border: 1px solid #d8e5ee;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 10px 24px rgba(24, 42, 58, .06);
    overflow-x: auto;
  }

  .unit-summary-table {
    width: 100%;
    min-width: 980px;
    border-collapse: separate;
    border-spacing: 0;
    color: #203847;

    th,
    td {
      border-bottom: 1px solid #e7eef3;
      padding: 12px 14px;
      text-align: right;
      white-space: nowrap;
    }

    thead th {
      position: sticky;
      top: 0;
      z-index: 1;
      background: #f7fbfd;
      color: #526b7b;
      font-size: $font-size-mini;
      font-weight: 900;
      text-transform: uppercase;

      &:before {
        content: '';
        display: inline-block;
        width: 8px;
        height: 8px;
        margin-right: 7px;
        border-radius: 50%;
        background: #8fa6b8;
        vertical-align: 1px;
      }

      &:first-child:before,
      &:last-child:before {
        display: none;
      }

      &.success:before { background: #3fd49b; }
      &.info:before { background: #54bed0; }
      &.neutral:before { background: #8fa6b8; }
      &.warning:before { background: #e7bf4f; }
      &.danger:before { background: #df6c8b; }
      &.matricula:before { background: #17a2b8; }
      &.rematricula:before { background: #7c5cff; }
    }

    tbody tr {
      transition: background .16s ease;

      &:hover {
        background: #f5fafc;
      }

      &:last-child {
        th,
        td {
          border-bottom: 0;
        }
      }
    }

    th:first-child {
      position: sticky;
      left: 0;
      z-index: 2;
      background: inherit;
      text-align: left;
    }

    thead th:first-child {
      background: #f7fbfd;
    }

    tbody th {
      color: #1f3340;
      font-size: 14px;
      font-weight: 900;
      text-transform: uppercase;
    }

    td {
      font-size: 14px;
      font-weight: 800;

      strong {
        color: #102f42;
        font-size: 15px;
        font-weight: 900;
      }
    }
  }

  .metric-pill {
    display: inline-flex;
    align-items: center;
    justify-content: flex-end;
    min-width: 42px;
    border-radius: 999px;
    padding: 5px 10px;
    font-weight: 900;

    &.success {
      background: #ddf8ed;
      color: #103a2a;
    }

    &.info {
      background: #e0f7fb;
      color: #123744;
    }

    &.neutral {
      background: #edf3f7;
      color: #203847;
    }

    &.warning {
      background: #fff6d8;
      color: #503d08;
    }

    &.danger {
      background: #fde5ec;
      color: #552133;
    }

    &.matricula {
      background: #def6fa;
      color: #0d4e5a;
    }

    &.rematricula {
      background: #ebe7ff;
      color: #31216f;
    }
  }

  .tracking-table {
    margin-top: $mg;

    > header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: $hmg_mid;
      margin-bottom: $hmg_mid;

      h2 {
        margin: 0;
        color: #1f3340;
        font-size: 18px;
        line-height: 1.2;
      }
    }
  }

  .table-filters {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: $hmg_mid;
    flex-wrap: wrap;

    label {
      display: grid;
      gap: 5px;
      color: #526b7b;
      font-size: $font-size-mini;
      font-weight: 900;
      text-transform: uppercase;
    }

    select {
      min-width: 150px;
      min-height: 34px;
      border: 1px solid #d8e5ee;
      border-radius: 6px;
      background: #ffffff;
      color: #203847;
      font-weight: 800;
      padding: 0 9px;
    }

  }

  .table-state {
    border: 1px solid #d8e5ee;
    border-radius: 8px;
    background: #ffffff;
    color: #526b7b;
    font-weight: 800;
    padding: $hmg_mid;

    &.error {
      background: #fff1f1;
      color: #9d1c1c;
    }
  }

  .table-loading-pill {
    width: max-content;
    border: 1px solid #d8e5ee;
    border-radius: 999px;
    background: #ffffff;
    color: #526b7b;
    font-size: $font-size-mini;
    font-weight: 900;
    margin: 0 0 $hmg_mid auto;
    padding: 6px 10px;
    text-transform: uppercase;
  }

  .table-wrap {
    max-width: 100%;
    min-width: 0;
    border: 1px solid #d8e5ee;
    border-radius: 8px;
    background: #ffffff;
    box-shadow: 0 10px 24px rgba(24, 42, 58, .05);
    overflow-x: auto;
  }

  table {
    width: 100%;
    min-width: 860px;
    border-collapse: collapse;

    th,
    td {
      border-bottom: 1px solid #e7eef3;
      color: #203847;
      padding: 11px $hmg_mid;
      text-align: left;
      vertical-align: top;
    }

    th {
      color: #526b7b;
      font-size: $font-size-mini;
      font-weight: 900;
      text-transform: uppercase;
      white-space: nowrap;
    }

    td {
      font-size: 13px;

      strong,
      span {
        display: block;
      }
    }

    tbody tr:last-child td {
      border-bottom: 0;
    }

    tbody tr.clickable-row {
      cursor: pointer;

      &:hover {
        background: #f7fbfd;
      }
    }

    .empty {
      color: #526b7b;
      font-weight: 800;
      text-align: center;
    }
  }

  .actions-col {
    width: 48px;
  }

  .icon-action {
    display: inline-grid;
    place-items: center;
    width: 32px;
    height: 32px;
    border: 1px solid #d8e5ee;
    border-radius: 6px;
    background: #ffffff;
    color: #326879;
    cursor: pointer;

    &:hover,
    &:focus {
      border-color: #54bed0;
      color: #123744;
      outline: 0;
    }

    &:disabled {
      cursor: not-allowed;
      opacity: .45;
    }
  }

  .table-status {
    width: max-content;
    border-radius: 999px;
    margin-top: 5px;
    padding: 3px 8px;
    font-size: 10px;
    font-weight: 900;
    text-transform: uppercase;

    &.success {
      background: #ddf8ed;
      color: #103a2a;
    }

    &.info {
      background: #e0f7fb;
      color: #123744;
    }

    &.neutral {
      background: #edf3f7;
      color: #203847;
    }

    &.warning {
      background: #fff6d8;
      color: #503d08;
    }

    &.danger {
      background: #fde5ec;
      color: #552133;
    }
  }

  .detail-overlay {
    position: fixed;
    inset: 0;
    z-index: 10000;
    display: flex;
    justify-content: flex-end;
    background: rgba(12, 24, 33, .38);
  }

  .detail-drawer {
    display: grid;
    grid-template-rows: auto minmax(0, 1fr);
    width: min(1040px, 92vw);
    height: 100%;
    background: #f6f9fb;
    box-shadow: -18px 0 46px rgba(12, 24, 33, .22);

    > header {
      display: flex;
      align-items: flex-start;
      justify-content: space-between;
      gap: $hmg_mid;
      border-bottom: 1px solid #d8e5ee;
      background: linear-gradient(135deg, #ffffff, #edf6f8);
      padding: $mg;

      span,
      small {
        display: block;
        color: #526b7b;
        font-size: $font-size-mini;
        font-weight: 900;
        text-transform: uppercase;
      }

      h2 {
        margin: 4px 0;
        color: #203847;
        font-size: 24px;
        line-height: 1.15;
      }

      button {
        display: inline-grid;
        place-items: center;
        min-width: 36px;
        height: 36px;
        border: 1px solid #d8e5ee;
        border-radius: 6px;
        background: #ffffff;
        color: #203847;
      }
    }
  }

  .detail-body {
    overflow-y: auto;
    padding: $mg;
  }

  .detail-summary {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: $hmg_mid;
    margin-bottom: $hmg_mid;

    div {
      min-height: 86px;
      border: 1px solid #d8e5ee;
      border-radius: 8px;
      background: #ffffff;
      padding: $hmg_mid;
    }

    span {
      display: block;
      margin-bottom: 7px;
      color: #526b7b;
      font-size: $font-size-mini;
      font-weight: 900;
      text-transform: uppercase;
    }

    strong {
      color: #203847;
      font-size: 14px;
      line-height: 1.35;
    }
  }

  .detail-section {
    border: 1px solid #d8e5ee;
    border-radius: 8px;
    background: #ffffff;
    margin-top: $hmg_mid;
    padding: $hmg_mid;

    h3 {
      margin: 0 0 $hmg_mid;
      color: #203847;
      font-size: 16px;
      line-height: 1.2;
    }

    dl {
      display: grid;
      grid-template-columns: 170px minmax(0, 1fr);
      gap: 8px $hmg_mid;
      margin: 0;
    }

    dt {
      color: #526b7b;
      font-size: $font-size-mini;
      font-weight: 900;
      text-transform: uppercase;
    }

    dd {
      min-width: 0;
      margin: 0;
      color: #203847;
      font-weight: 800;
      overflow-wrap: anywhere;
    }
  }

  .detail-list {
    display: grid;
    gap: $hmg_mini;

    article {
      border: 1px solid #e7eef3;
      border-radius: 8px;
      background: #fbfdfe;
      padding: $hmg_mid;
    }

    strong,
    small,
    span,
    em {
      display: block;
    }

    strong {
      color: #203847;
      font-size: 14px;
    }

    small,
    span,
    em {
      margin-top: 4px;
      color: #526b7b;
      font-size: $font-size-mini;
      font-style: normal;
      font-weight: 800;
      overflow-wrap: anywhere;
    }

    &.danger-list article {
      border-color: #f0bdca;
      background: #fff7f9;
    }
  }

  .detail-empty {
    margin: 0;
    color: #526b7b;
    font-weight: 800;
  }

  .stage-timeline,
  .event-timeline {
    display: grid;
    gap: $hmg_mid;
    margin: 0;
    padding: 0;

    li {
      list-style: none;
    }
  }

  .stage-timeline {
    li {
      display: grid;
      grid-template-columns: 12px minmax(0, 1fr);
      gap: $hmg_mid;
    }

    i {
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background: #8fa6b8;
      margin-top: 4px;

      &.success { background: #3fd49b; }
      &.info { background: #54bed0; }
      &.neutral { background: #8fa6b8; }
      &.warning { background: #e7bf4f; }
      &.danger { background: #df6c8b; }
    }

    strong,
    small,
    p {
      display: block;
    }

    strong {
      color: #203847;
    }

    small,
    p {
      margin: 4px 0 0;
      color: #526b7b;
      font-size: $font-size-mini;
      font-weight: 800;
    }

    p {
      color: #8f2942;
    }

    span {
      display: inline-flex;
      border-radius: 999px;
      background: #edf3f7;
      color: #526b7b;
      font-size: 10px;
      font-weight: 900;
      margin: 8px 6px 0 0;
      padding: 4px 8px;
    }
  }

  .event-timeline {
    li {
      display: grid;
      grid-template-columns: 138px minmax(0, 1fr);
      gap: $hmg_mid;
      border-bottom: 1px solid #e7eef3;
      padding-bottom: $hmg_mid;

      &:last-child {
        border-bottom: 0;
        padding-bottom: 0;
      }
    }

    time {
      color: #526b7b;
      font-size: $font-size-mini;
      font-weight: 900;
    }

    strong,
    small,
    p,
    em {
      display: block;
      overflow-wrap: anywhere;
    }

    strong {
      color: #203847;
    }

    small,
    em {
      margin-top: 4px;
      color: #526b7b;
      font-size: $font-size-mini;
      font-style: normal;
      font-weight: 800;
    }

    p {
      margin: 5px 0 0;
      color: #8f2942;
      font-size: $font-size-mini;
      font-weight: 800;
    }
  }

  .table-pagination {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: $hmg_mid;
    flex-wrap: wrap;
    margin-top: $hmg_mid;

    span,
    strong {
      color: #526b7b;
      font-size: $font-size-mini;
      font-weight: 900;
    }

    div {
      display: flex;
      align-items: center;
      gap: $hmg_mini;
      flex-wrap: wrap;
    }

    button {
      min-height: 32px;
      border: 1px solid #d8e5ee;
      border-radius: 6px;
      background: #ffffff;
      color: #203847;
      font-size: $font-size-mini;
      font-weight: 900;
      padding: 0 $hmg_mini;

      &:disabled {
        cursor: not-allowed;
        opacity: .45;
      }
    }
  }

  @media screen and (max-width: 720px) {
    .tracking-matriculas {
      padding: $hmg_mini;
    }

    .signal-grid {
      grid-template-columns: 1fr;
    }

    .unit-chart {
      > header {
        align-items: flex-start;
        flex-direction: column;
      }
    }

    .unit-summary-table {
      min-width: 860px;

      th,
      td {
        padding: 10px 12px;
      }
    }

    .tracking-table > header {
      align-items: flex-start;
      flex-direction: column;
    }

    .table-filters,
    .table-filters label,
    .table-filters select {
      width: 100%;
    }

    .detail-drawer {
      width: 100vw;

      > header {
        padding: $hmg_mid;

        h2 {
          font-size: 20px;
        }
      }
    }

    .detail-body {
      padding: $hmg_mid;
    }

    .detail-summary {
      grid-template-columns: 1fr;
    }

    .detail-section {
      dl {
        grid-template-columns: 1fr;
      }
    }

    .event-timeline {
      li {
        grid-template-columns: 1fr;
      }
    }
  }
</style>

<style lang="scss">
  body.tracking-detail-open {
    overflow: hidden;

    .appController {
      .main-content {
        overflow: hidden !important;
      }
    }
  }
</style>
